// @flow

import {Grid, makeStyles} from '@material-ui/core';
import React, {useRef} from 'react';
import FunnelColumnHeader from './FunnelColumnHeader';
import ConversationCard from '../../ConversationCard/ConversationCard';
import AddNewCardModal from '../../Modals/AddNewCardModal';

import {Draggable, Droppable} from 'react-beautiful-dnd';

import type {MultichatFunnelLane} from '../../../../../../entities';
import {useCurrentMultichatFunnelActions} from '../../../utils';
import {useNearScreen, useDebounceEffect} from '../../../../../../utils/react';
import InlineLoader from '../../../../../../elements/LoadingSpinner/InlineLoader';
import {useStoreDevices} from '../../../../chat/utils';

const useStyles = makeStyles(theme => ({
  column: {
    flexShrink: 0,
    width: 280,
    height: '100%',
  },
  content: {
    borderRadius: 12,
    backgroundColor: '#EAEBF1',
    height: '100%',
    padding: '.5rem',
  },
  header: {
    borderRadius: 12,
  },
  cardList: {
    height: '70vh',
    overflowY: 'auto',
    gap: '.5rem',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    transition: 'background-color 0.2s ease',
    borderRadius: 8,
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  cardListDraggingOver: {
    backgroundColor: 'rgba(23, 116, 255, 0.1)',
  },
  cardListDragging: {
    border: '2px dashed rgba(23, 116, 255, 0.3)',
  },
  card: {
    height: 'min-content',
    width: '100%',
  },
  footer: {
    position: 'sticky',
    bottom: 0,
    width: '100%',
    background: '#EAEBF1',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 40,
    width: '100%',
  },
}));

type Props = {
  lane: MultichatFunnelLane,
  onEdit?: () => void,
  onDelete?: () => void,
  dragHandleProps?: any,
  isDraggingCard?: boolean,
  search?: string,
};

export default function FunnelColumn(props: Props) {
  const {
    lane,
    onEdit,
    onDelete,
    dragHandleProps,
    isDraggingCard,
    search,
  } = props;

  const classes = useStyles();

  const cardListRef = useRef<HTMLElement | null>(null);

  const {
    loading,
    changeMainFunnelLane,
    addLaneConversationsPage,
  } = useCurrentMultichatFunnelActions(search);
  const {storeDevices} = useStoreDevices();

  const handleStarClick = () => {
    changeMainFunnelLane(lane.id);
  };

  const handleIsArchivedConversation = (deviceId: string) => {
    const deviceFound = storeDevices.find(device => device.id === deviceId);
    if (!deviceFound) return false;

    if (
      deviceFound.state === 'ARCHIVED' ||
      deviceFound.state === 'DISCONNECTED'
    )
      return true;

    return false;
  };

  const {fromRef: paginationFlagRef, isNearScreen} = useNearScreen({
    root: cardListRef.current,
    distance: '100px',
    once: false,
  });

  useDebounceEffect(
    () => {
      if (isNearScreen) {
        addLaneConversationsPage(lane.id);
      }
    },
    750,
    [isNearScreen, lane.id, lane.stage.label]
  );

  const isMain = !!lane.setting;

  return (
    <div className={classes.column}>
      <div className={classes.content}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <FunnelColumnHeader
              count={lane?.conversationPagination?.total || 0}
              isMain={isMain}
              name={lane.stage.label}
              color={lane.stage.color}
              onStarClick={handleStarClick}
              onEdit={onEdit}
              onDelete={onDelete}
              dragHandleProps={dragHandleProps}
            />
          </Grid>

          <Grid item xs={12}>
            <Droppable droppableId={lane.id}>
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  <div
                    className={`${classes.cardList} ${
                      isDraggingCard ? classes.cardListDragging : ''
                    } ${
                      snapshot.isDraggingOver
                        ? classes.cardListDraggingOver
                        : ''
                    }`}
                    ref={cardListRef}
                  >
                    {lane?.conversations?.map((conversation, index) => (
                      <Draggable
                        key={`${conversation.id}-${conversation.createdAt}`}
                        draggableId={conversation.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={classes.card}
                          >
                            <ConversationCard
                              conversation={conversation}
                              isArchivedConversation={handleIsArchivedConversation(
                                conversation.deviceId
                              )}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}

                    <span ref={paginationFlagRef}></span>

                    {loading && (
                      <div className={classes.loader}>
                        <InlineLoader loading size={40} />
                      </div>
                    )}
                  </div>
                  {provided.placeholder}
                  {isMain && (
                    <div className={classes.footer}>
                      <AddNewCardModal />
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
